import { Upload } from 'tus-js-client';
import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';

/**
 * Custom HTTP Stack for tus-js-client with iOS compatibility
 * Uses Capacitor HTTP plugin on native platforms for better iOS ATS compatibility
 * Falls back to XMLHttpRequest on web
 */
class CustomTusHttpStack {
  createRequest(method, url) {
    return new CustomTusRequest(method, url);
  }

  getName() {
    return 'CustomTusHttpStack';
  }
}

class CustomTusRequest {
  constructor(method, url) {
    this._method = method;
    this._url = url;
    this._headers = {};
    this._xhr = null;

    // Use Capacitor HTTP for all requests on native platforms (bypasses CORS)
    const isNative = Capacitor.isNativePlatform();
    this._useCapacitorHttp = isNative;

    // Create XHR as fallback for web or if Capacitor HTTP fails
    if (!this._useCapacitorHttp) {
      this._createXHR();
    }

    console.log(`TUS Request: ${method} ${url} - Using ${this._useCapacitorHttp ? 'Capacitor HTTP' : 'XMLHttpRequest'}`);
  }

  _createXHR() {
    this._xhr = new XMLHttpRequest();
    // Don't call open() here - we'll do it in send() after setting up event handlers
  }

  getMethod() {
    return this._method;
  }

  getURL() {
    return this._url;
  }

  // Custom method to set URL - this is what was missing in the original implementation
  setURL(url) {
    if (url !== this._url) {
      console.log('CustomTusRequest: Changing URL from', this._url, 'to', url);

      try {
        this._url = url;
        // Recreate XMLHttpRequest with new URL
        this._createXHR();

        // Re-apply all headers
        Object.keys(this._headers).forEach(header => {
          try {
            this._xhr.setRequestHeader(header, this._headers[header]);
          } catch (headerError) {
            console.warn('CustomTusRequest: Failed to re-apply header', header, ':', headerError);
          }
        });

        console.log('CustomTusRequest: Successfully updated URL to:', url);
      } catch (error) {
        console.error('CustomTusRequest: Failed to update URL:', error);
        // Fallback: keep the original URL and XHR
        throw new Error(`Failed to update request URL: ${error.message}`);
      }
    }
  }

  setHeader(header, value) {
    this._headers[header] = value;
    // Headers will be set in send() after open() is called
  }

  getHeader(header) {
    return this._headers[header];
  }

  setProgressHandler(progressHandler) {
    this._progressHandler = progressHandler;

    // Only set on XHR if we're using XMLHttpRequest
    if (!this._useCapacitorHttp && this._xhr && 'upload' in this._xhr) {
      this._xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) {
          return;
        }
        progressHandler(e.loaded);
      };
    }
    // Note: Capacitor HTTP doesn't support upload progress tracking
    // Progress will be simulated or handled differently
  }

  send(body = null) {
    // Use Capacitor HTTP on native platforms for better iOS compatibility
    if (this._useCapacitorHttp) {
      return this._sendWithCapacitorHttp(body);
    }

    // Use XMLHttpRequest on web
    return new Promise((resolve, reject) => {
      // Set up event handlers BEFORE opening the request
      this._xhr.onload = () => {
        console.log(`TUS XHR: ${this._method} ${this._url} completed with status ${this._xhr.status}`);
        resolve(new CustomTusResponse(this._xhr));
      };

      this._xhr.onerror = (err) => {
        console.error(`TUS XHR: ${this._method} ${this._url} failed:`, err);
        console.error('TUS XHR: Network error - possible CORS issue or server unreachable');
        reject(new Error(`Network request failed: ${this._method} ${this._url}`));
      };

      this._xhr.ontimeout = () => {
        console.error(`TUS XHR: ${this._method} ${this._url} timed out`);
        reject(new Error(`Request timeout: ${this._method} ${this._url}`));
      };

      this._xhr.onabort = () => {
        console.error(`TUS XHR: ${this._method} ${this._url} was aborted`);
        reject(new Error(`Request aborted: ${this._method} ${this._url}`));
      };

      // Now open the request
      this._xhr.open(this._method, this._url, true);

      // Set all headers after opening
      Object.keys(this._headers).forEach(header => {
        this._xhr.setRequestHeader(header, this._headers[header]);
      });

      console.log(`TUS XHR: Sending ${this._method} to ${this._url} with headers:`, this._headers);
      this._xhr.send(body);
    });
  }

  /**
   * Send request using Capacitor HTTP (for native iOS/Android)
   * This bypasses iOS ATS restrictions and provides better compatibility
   */
  async _sendWithCapacitorHttp(body) {
    try {
      console.log(`TUS: Using Capacitor HTTP for ${this._method} ${this._url}`);

      const options = {
        url: this._url,
        method: this._method,
        headers: this._headers,
        responseType: 'text'
      };

      // Handle binary data for PATCH requests
      if (this._method === 'PATCH' && body) {
        console.log('TUS: PATCH body type:', typeof body, 'constructor:', body.constructor?.name);
        console.log('TUS: Body has slice?', typeof body.slice === 'function', 'has size?', typeof body.size === 'number');

        // Check if it's a Blob-like object (File, Blob, or anything with slice method)
        const isBlobLike = body instanceof Blob ||
                          body instanceof File ||
                          (typeof body.slice === 'function' && typeof body.size === 'number');

        const isArrayBuffer = body instanceof ArrayBuffer ||
                             (body.buffer && body.buffer instanceof ArrayBuffer);

        if (isBlobLike) {
          console.log('TUS: Sending Blob-like object as binary via Capacitor HTTP, size:', body.size);
          // Send as Blob directly - Capacitor HTTP should handle it
          options.data = body;
        } else if (isArrayBuffer) {
          console.log('TUS: Sending ArrayBuffer/TypedArray as binary via Capacitor HTTP');
          // For Uint8Array and similar, convert to regular Array for Capacitor HTTP
          const actualBuffer = body.buffer || body;
          // Convert TypedArray to regular Array of numbers
          const uint8Array = new Uint8Array(actualBuffer);
          const arrayData = Array.from(uint8Array);
          console.log('TUS: Converted to array, length:', arrayData.length);
          options.data = arrayData;
        } else {
          console.warn('TUS: PATCH body type not recognized, attempting direct send');
          options.data = body;
        }
      } else {
        options.data = body;
      }

      const response = await CapacitorHttp.request(options);

      console.log(`TUS: Capacitor HTTP response: ${response.status}`);

      return new CustomTusCapacitorResponse(response);

    } catch (error) {
      console.error('TUS: Capacitor HTTP error:', error);
      throw error;
    }
  }

  /**
   * Convert Blob to base64 string
   */
  async _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  abort() {
    if (!this._useCapacitorHttp && this._xhr) {
      this._xhr.abort();
    }
    // Capacitor HTTP doesn't support request abortion
    return Promise.resolve();
  }

  getUnderlyingObject() {
    return this._xhr;
  }
}

class CustomTusResponse {
  constructor(xhr) {
    this._xhr = xhr;
  }

  getStatus() {
    return this._xhr.status;
  }

  getHeader(header) {
    return this._xhr.getResponseHeader(header);
  }

  getBody() {
    return this._xhr.responseText;
  }

  getUnderlyingObject() {
    return this._xhr;
  }
}

/**
 * Response wrapper for Capacitor HTTP plugin
 */
class CustomTusCapacitorResponse {
  constructor(response) {
    this._response = response;
  }

  getStatus() {
    return this._response.status;
  }

  getHeader(header) {
    // Capacitor HTTP returns headers as object, case-insensitive lookup
    const headers = this._response.headers || {};
    const lowerHeader = header.toLowerCase();

    // Try exact match first
    if (headers[header]) return headers[header];

    // Try case-insensitive match
    for (const key in headers) {
      if (key.toLowerCase() === lowerHeader) {
        return headers[key];
      }
    }

    return null;
  }

  getBody() {
    return this._response.data || '';
  }

  getUnderlyingObject() {
    return this._response;
  }
}

/**
 * TUS-based Construction Image Upload Service
 * Handles resumable uploads using TUS protocol with project-based organization
 * and timestamp overlay functionality
 */

export class TusConstructionImageUploadService {
  constructor() {
    // Get TUS server URL from environment and enforce HTTP protocol
    const rawEndpoint = import.meta.env.VITE_TUS_SERVER_URL || 'http://localhost:3001/upload';

    // Force HTTP protocol to prevent SSL errors with Docker-based TUS server
    this.endpoint = this.enforceHttpProtocol(rawEndpoint);
    this.activeUploads = new Map();
    this.uploadMetadata = new Map(); // Store resumption metadata

    // Initialize custom HTTP stack for URL modification support
    this.httpStack = new CustomTusHttpStack();

    // Validate and log TUS configuration
    this.validateConfiguration();
    console.log('TUS: Initialized service with endpoint:', this.endpoint);
    console.log('TUS: VITE_TUS_SERVER_URL env var:', import.meta.env.VITE_TUS_SERVER_URL);
    console.log('TUS: Enforced HTTP protocol:', this.endpoint !== rawEndpoint ? 'YES' : 'NO');
    console.log('TUS: Using custom HTTP stack:', this.httpStack.getName());

    // Clear any stored URLs with wrong protocol to prevent SSL errors
    this.clearInvalidStoredUrls();

    // Initialize URL storage for TUS resumable uploads
    this.initializeUrlStorage();
  }

  /**
   * Validate TUS service configuration
   */
  validateConfiguration() {
    try {
      // Validate endpoint URL
      new URL(this.endpoint);

      // Warn if HTTPS is being used for local/Docker development
      const url = new URL(this.endpoint);
      if (url.protocol === 'https:' && (url.hostname === 'localhost' || url.hostname.match(/^192\.168\./))) {
        console.warn('TUS: WARNING - Using HTTPS with local/Docker server may cause SSL errors');
        console.warn('TUS: Consider using HTTP for Docker-based TUS servers');
      }

      // Check if endpoint ends with /upload
      if (!this.endpoint.endsWith('/upload')) {
        console.warn('TUS: WARNING - Endpoint should end with /upload for tus.io protocol');
      }

      console.log('TUS: Configuration validation passed');
    } catch (error) {
      console.error('TUS: Invalid endpoint URL configuration:', error.message);
      throw new Error(`Invalid TUS endpoint configuration: ${error.message}`);
    }
  }

  /**
   * Enforce HTTP protocol for Docker-based TUS server
   * @param {string} url - The URL to process
   * @returns {string} URL with HTTP protocol
   */
  enforceHttpProtocol(url) {
    try {
      const urlObj = new URL(url);
      if (urlObj.protocol === 'https:' && urlObj.hostname.includes('192.168.1')) {
        urlObj.protocol = 'http:';
        console.log('TUS: Converted HTTPS to HTTP for Docker TUS server:', url, '->', urlObj.toString());
      }
      return urlObj.toString();
    } catch (error) {
      console.warn('TUS: Invalid URL format, using as-is:', url);
      return url;
    }
  }

  /**
   * Clear stored URLs that don't match current endpoint protocol
   */
  clearInvalidStoredUrls() {
    try {
      const currentProtocol = new URL(this.endpoint).protocol;
      const keys = Object.keys(localStorage);

      keys.forEach(key => {
        if (key.startsWith('tus_url_')) {
          const storedUrl = localStorage.getItem(key);
          if (storedUrl) {
            try {
              const storedProtocol = new URL(storedUrl).protocol;
              if (storedProtocol !== currentProtocol) {
                console.log(`TUS: Clearing invalid stored URL (${storedProtocol} vs ${currentProtocol}):`, key);
                localStorage.removeItem(key);
              }
            } catch (e) {
              // Invalid URL, remove it
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.warn('TUS: Failed to clear invalid URLs:', error);
    }
  }

  initializeUrlStorage() {
    // Custom URL storage implementation for persistence across navigation/app states
    this.urlStorage = {
      setItem: (key, value) => {
        try {
          localStorage.setItem(`tus_url_${key}`, value);
          console.log('TUS: Stored upload URL for key:', key);
        } catch (error) {
          console.warn('TUS: Failed to store URL:', error);
        }
      },
      getItem: (key) => {
        try {
          const value = localStorage.getItem(`tus_url_${key}`);
          if (value) {
            console.log('TUS: Retrieved upload URL for key:', key);
          }
          return value;
        } catch (error) {
          console.warn('TUS: Failed to retrieve URL:', error);
          return null;
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(`tus_url_${key}`);
          console.log('TUS: Removed upload URL for key:', key);
        } catch (error) {
          console.warn('TUS: Failed to remove URL:', error);
        }
      }
    };
  }

  /**
   * Upload images using TUS resumable uploads
   * @param {Object} uploadData - Upload data with images, construction, etc.
   * @param {Object} callbacks - Progress and status callbacks
   * @returns {Promise<Object>} Upload result with control functions
   */
  async uploadImages(uploadData, callbacks = {}) {
    const {
      onProgress,
      onComplete,
      onError,
      onImageProgress,
      onImageComplete
    } = callbacks;

    // Validate input
    if (!uploadData.images || uploadData.images.length === 0) {
      throw new Error('No images provided for upload');
    }

    if (!uploadData.construction) {
      throw new Error('Construction site is required');
    }

    const projectId = this.generateProjectId(uploadData.construction);
    const uploads = [];
    const results = {
      uploads: [],
      completed: [],
      failed: [],
      abort: () => this.abortAllUploads(uploads)
    };

    try {
      // Process each image
      for (let i = 0; i < uploadData.images.length; i++) {
        const image = uploadData.images[i];
        const uploadResult = await this.uploadSingleImage(
          image,
          uploadData,
          projectId,
          i,
          {
            onProgress: (progress) => {
              if (onImageProgress) {
                onImageProgress(i, progress);
              }
              this.updateOverallProgress(uploads, onProgress);
            },
            onComplete: (result) => {
              results.completed.push(result);
              if (onImageComplete) {
                onImageComplete(i, result);
              }
              this.updateOverallProgress(uploads, onProgress);
            },
            onError: (error) => {
              results.failed.push({ imageIndex: i, error });
              if (onError) {
                onError(error, i);
              }
            }
          }
        );

        uploads.push(uploadResult);
        results.uploads.push(uploadResult);
      }

      // Wait for all uploads to complete
      await Promise.allSettled(uploads.map(upload => upload.promise));

      if (results.failed.length === 0) {
        if (onComplete) {
          onComplete({
            projectId,
            totalImages: uploadData.images.length,
            completed: results.completed
          });
        }
      }

      return results;

    } catch (error) {
      console.error('TUS upload failed:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  /**
   * Upload a single image with TUS
   * @param {Object} image - Image data
   * @param {Object} uploadData - Upload metadata
   * @param {string} projectId - Project identifier
   * @param {number} imageIndex - Index of the image
   * @param {Object} callbacks - Progress callbacks
   * @returns {Object} Upload control object
   */
  async uploadSingleImage(image, uploadData, projectId, imageIndex, callbacks) {
    const { onProgress, onComplete, onError } = callbacks;

    // Generate unique filename
    const timestamp = Date.now();
    const userId = 'user_' + timestamp; // Simplified user ID
    let filename = '';

    if (uploadData.name) {
      filename += `${uploadData.name}_`;
    }
    filename += `${uploadData.date || new Date().toISOString().split('T')[0]}_${timestamp}_${userId}_${imageIndex + 1}.${image.format || 'jpg'}`;

    // Convert base64 to blob
    let imageBlob;
    try {
      imageBlob = await this.prepareImageBlob(image, uploadData);
    } catch (error) {
      throw new Error(`Failed to prepare image ${imageIndex + 1}: ${error.message}`);
    }

    // Prepare metadata for TUS
    const metadata = {
      projectId: projectId,
      filename: filename,
      originalName: filename,
      imageIndex: imageIndex.toString(),
      totalImages: uploadData.images.length.toString(),
      constructionSite: uploadData.construction.label || 'Unknown Site'
    };

    // Add timestamp overlay metadata if enabled - server will handle the overlay processing
    if (uploadData.addDateTime && image.caption) {
      metadata.addTimestamp = 'true';
      metadata.timestampText = image.caption;
      metadata.position = 'bottom-right';  // Server default is bottom-right
      metadata.fontSize = '14';            // Increase font size for better mobile visibility
      metadata.fontColor = 'white';        // Server default is white
      metadata.backgroundColor = 'rgba(0,0,0,0.7)';  // Semi-transparent background for readability
      metadata.padding = '10';             // Server default padding
    }

    const uploadId = this.generateUploadId();
    let uploadProgress = 0;

    // Store metadata for potential resumption
    const resumptionKey = `${uploadId}_${imageIndex}`;
    this.uploadMetadata.set(resumptionKey, {
      uploadId,
      imageIndex,
      metadata,
      blob: imageBlob,
      filename,
      projectId
    });

    // Create a promise that resolves when the actual TUS upload completes
    const uploadPromise = new Promise((resolveUpload, rejectUpload) => {
      console.log(`TUS: Creating upload instance for ${filename}...`);
      console.log('TUS: Upload configuration:', {
        endpoint: this.endpoint,
        blobSize: imageBlob.size,
        blobType: imageBlob.type,
        metadata: metadata
      });

      const upload = new Upload(imageBlob, {
        endpoint: this.endpoint,
        metadata: metadata,
        retryDelays: [0, 3000, 5000, 10000, 20000], // Retry delays in ms
        httpStack: this.httpStack, // Use our custom HTTP stack with setURL support
        // Disable URL storage temporarily to fix retry loop
        // urlStorage: this.urlStorage,
        fingerprint: (file, options) => {
          // Create a unique fingerprint for this upload (simplified)
          const fingerprint = `${uploadId}_${imageIndex}_${file.size}_${Date.now()}`;
          console.log('TUS: Generated fingerprint:', fingerprint, 'for file size:', file.size);

          // For now, don't use stored URLs to avoid retry loops
          // TODO: Re-enable resumable uploads after fixing the retry loop
          const existingUrl = this.urlStorage.getItem(fingerprint);
          if (existingUrl) {
            console.log('TUS: Clearing existing URL to prevent retry loop:', existingUrl);
            this.urlStorage.removeItem(fingerprint);
          }

          return Promise.resolve(fingerprint);
        },
        storeFingerprintForResuming: true,
        onUploadUrlAvailable: (uploadUrl) => {
          // Force HTTP protocol on the upload URL returned by server
          const correctedUrl = this.enforceHttpProtocol(uploadUrl);
          console.log(`TUS: Upload URL available for ${filename}:`, uploadUrl);
          if (correctedUrl !== uploadUrl) {
            console.log('TUS: Corrected URL protocol from HTTPS to HTTP:', correctedUrl);
            // Override the upload URL in the client
            upload.url = correctedUrl;
          }
          console.log('TUS: POST request successful, ready to send PATCH requests');

          // Store the upload URL manually since the callback might not be working correctly
          if (!uploadUrl && upload.url) {
            console.log('TUS: Using upload.url as fallback:', upload.url);
          }
        },
        onChunkComplete: (chunkSize, bytesAccepted, bytesTotal) => {
          console.log(`TUS: Chunk completed for ${filename}: ${chunkSize} bytes, ${bytesAccepted}/${bytesTotal} total`);
          console.log('TUS: PATCH request completed successfully');
        },
        onBeforeRequest: (req) => {
          const originalUrl = req.getURL();
          const correctedUrl = this.enforceHttpProtocol(originalUrl);

          console.log(`TUS: About to send ${req.getMethod()} request to:`, originalUrl);

          // Force HTTP protocol if the request URL was HTTPS
          if (correctedUrl !== originalUrl) {
            console.log('TUS: Correcting request URL from HTTPS to HTTP:', correctedUrl);
            // Now we can use setURL() with our custom HTTP stack
            req.setURL(correctedUrl);
          }

          if (req.getMethod() === 'POST') {
            console.log('TUS: Sending initial POST to create upload session');
          } else if (req.getMethod() === 'PATCH') {
            console.log('TUS: Sending PATCH request with file data');
          } else if (req.getMethod() === 'HEAD') {
            console.log('TUS: Sending HEAD request to check upload offset');
          }
        },
        onAfterResponse: (req, res) => {
          const method = req.getMethod();
          const status = res.getStatus();
          const location = res.getHeader('location');

          console.log(`TUS: ${method} response:`, status, location || 'no location header');

          if (method === 'POST' && status === 201) {
            console.log('TUS: Upload session created successfully');
            if (location) {
              console.log('TUS: Upload URL from Location header:', location);
            } else {
              console.error('TUS: POST response missing Location header!');
            }
          } else if (method === 'HEAD') {
            const uploadOffset = res.getHeader('upload-offset');
            const uploadLength = res.getHeader('upload-length');
            console.log(`TUS: HEAD response - Offset: ${uploadOffset}, Length: ${uploadLength}`);

            // If offset is 0 and we have length, upload should start PATCH requests
            if (uploadOffset === '0' && uploadLength) {
              console.log('TUS: Upload ready to start - should send PATCH requests now');
            }
          } else if (method === 'PATCH') {
            console.log('TUS: PATCH request completed successfully');
          }
        },
        onError: (error) => {
          this.activeUploads.delete(uploadId);
          console.error(`TUS upload failed for image ${imageIndex + 1}:`, error);

          let errorMessage = error.message;
          if (error.originalResponse) {
            const status = error.originalResponse.getStatus();
            switch (status) {
              case 400:
                errorMessage = 'Invalid request - check image format';
                break;
              case 413:
                errorMessage = 'File too large - max size is 10MB';
                break;
              case 415:
                errorMessage = 'Unsupported file type - images only';
                break;
              default:
                errorMessage = `Upload failed with status ${status}`;
            }
          }

          if (onError) onError(new Error(errorMessage));
          rejectUpload(new Error(errorMessage));
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          uploadProgress = (bytesUploaded / bytesTotal * 100);
          console.log(`TUS: Upload progress for ${filename}: ${uploadProgress.toFixed(2)}% (${bytesUploaded}/${bytesTotal} bytes)`);

          // Log the first progress event to confirm PATCH requests are working
          if (bytesUploaded === 0) {
            console.log('TUS: First progress event - upload starting to send data');
          } else if (bytesUploaded > 0) {
            console.log('TUS: PATCH request successfully sending data');
          }

          if (onProgress) {
            onProgress({
              uploadId,
              imageIndex,
              bytesUploaded,
              bytesTotal,
              percentage: uploadProgress.toFixed(2)
            });
          }
        },
        onSuccess: async () => {
          this.activeUploads.delete(uploadId);
          this.uploadMetadata.delete(resumptionKey);

          // Clean up URL storage
          if (this.urlStorage && upload.fingerprint) {
            this.urlStorage.removeItem(upload.fingerprint);
          }

          const result = {
            uploadId,
            imageIndex,
            uploadUrl: upload.url,
            downloadUrl: this.getDownloadUrl(projectId, filename),
            filename,
            projectId
          };

          console.log(`TUS: Upload completed successfully for ${filename}:`, result);

          // Trigger FTP upload to remote server via Parse Cloud function
          try {
            console.log(`TUS: Calling uploadConstructionImageToFtp for ${filename}...`);

            const Parse = (await import('parse')).default;

            // Get the construction object - could be a Parse Object or a plain object
            const constructionData = uploadData.construction.value || uploadData.construction;

            // If it's a Parse Object, we need to pass it by fetching from Parse using objectId
            let constructionParam;
            if (constructionData.className === 'Construction' || constructionData.id) {
              // Pass objectId and let cloud function fetch it
              constructionParam = {
                objectId: constructionData.id,
                className: constructionData.className || 'Construction'
              };
            } else {
              // It's already a plain object
              constructionParam = constructionData;
            }

            const ftpResult = await Parse.Cloud.run('uploadConstructionImageToFtp', {
              construction: constructionParam,
              filePath: `${projectId}/${filename}`
            });

            console.log(`TUS: FTP upload successful for ${filename}:`, ftpResult);
            result.ftpUpload = ftpResult;
          } catch (ftpError) {
            console.error(`TUS: FTP upload failed for ${filename}:`, ftpError);
            result.ftpUploadError = ftpError.message;
            // Don't fail the entire upload if FTP fails - just log it
          }

          if (onComplete) onComplete(result);
          resolveUpload(result);
        }
      });

      // Store the upload for potential cancellation
      this.activeUploads.set(uploadId, upload);

      console.log(`TUS: Starting upload for ${filename} to ${this.endpoint}`);

      // Start the upload - this will trigger POST then PATCH requests
      try {
        upload.start();
        console.log(`TUS: upload.start() called successfully for ${filename}`);

        // Check if upload was stored properly
        const storedUpload = this.activeUploads.get(uploadId);
        if (storedUpload) {
          console.log(`TUS: Upload stored in activeUploads with url:`, storedUpload.url || 'undefined');
        } else {
          console.error(`TUS: Failed to store upload in activeUploads for ${uploadId}`);
        }
      } catch (error) {
        console.error(`TUS: Error calling upload.start() for ${filename}:`, error);
        rejectUpload(error);
        return;
      }
    });

    // Return the control object with the actual upload promise
    return {
      uploadId,
      upload: this.activeUploads.get(uploadId),
      promise: uploadPromise,
      abort: () => this.abortUpload(uploadId)
    };
  }

  /**
   * Prepare image blob from base64 data
   * @param {Object} image - Image data
   * @param {Object} uploadData - Upload configuration
   * @returns {Promise<Blob>} Image blob ready for upload
   */
  async prepareImageBlob(image, uploadData) {
    try {
      // Use original image data - server will handle timestamp overlay if requested via metadata
      const imageData = image.base64String;

      if (!imageData) {
        throw new Error('No base64 image data provided');
      }

      console.log(`TUS: Preparing blob for image with ${imageData.length} base64 characters`);

      // Convert base64 to blob
      const byteCharacters = atob(imageData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: `image/${image.format || 'jpeg'}` });
      console.log(`TUS: Created blob with size ${blob.size} bytes and type ${blob.type}`);

      return blob;
    } catch (error) {
      console.error('TUS: Failed to prepare image blob:', error);
      throw new Error(`Failed to prepare image blob: ${error.message}`);
    }
  }


  /**
   * Generate project ID from construction data
   * @param {Object} construction - Construction site data
   * @returns {string} Project ID
   */
  generateProjectId(construction) {
    const siteId = construction.value?.id || construction.id || 'unknown';
    const siteName = construction.label || construction.name || 'site';

    // Create a clean project ID (alphanumeric, hyphens, underscores)
    const cleanName = siteName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);

    const projectId = `${cleanName}`.replace(/--+/g, '-');

    // Ensure total length doesn't exceed 100 chars
    return projectId.length > 100 ? projectId.substring(0, 100) : projectId;
  }

  /**
   * Update overall progress across all uploads
   * @param {Array} uploads - Array of upload objects
   * @param {Function} onProgress - Progress callback
   */
  updateOverallProgress(uploads, onProgress) {
    if (!onProgress || uploads.length === 0) return;

    // Calculate overall progress (this is simplified - real implementation would track individual progress)
    const totalUploads = uploads.length;
    const completedUploads = uploads.filter(u => u.completed).length;
    const overallProgress = (completedUploads / totalUploads) * 100;

    onProgress({
      overallProgress: overallProgress.toFixed(2),
      completedUploads,
      totalUploads,
      activeUploads: totalUploads - completedUploads
    });
  }

  /**
   * Resume an upload from stored metadata
   * @param {string} resumptionKey - The resumption key
   * @param {Object} callbacks - Upload callbacks
   * @returns {Promise<Object>} Upload control object
   */
  async resumeUpload(resumptionKey, callbacks = {}) {
    const metadata = this.uploadMetadata.get(resumptionKey);
    if (!metadata) {
      throw new Error(`No metadata found for resumption key: ${resumptionKey}`);
    }

    console.log('TUS: Resuming upload for key:', resumptionKey);

    // Re-create the upload with the same parameters
    return this.uploadSingleImage(
      { base64String: metadata.blob, format: 'jpeg' }, // Simplified for resumption
      { construction: { label: 'Resumed Upload' } }, // Simplified
      metadata.projectId,
      metadata.imageIndex,
      callbacks
    );
  }

  /**
   * Abort a specific upload
   * @param {string} uploadId - Upload ID to abort
   */
  abortUpload(uploadId) {
    const upload = this.activeUploads.get(uploadId);
    if (upload) {
      try {
        upload.abort();
        this.activeUploads.delete(uploadId);

        // Clean up metadata
        for (const [key, metadata] of this.uploadMetadata.entries()) {
          if (metadata.uploadId === uploadId) {
            this.uploadMetadata.delete(key);
          }
        }

        console.log(`Aborted upload: ${uploadId}`);
      } catch (error) {
        console.warn(`Failed to abort upload ${uploadId}:`, error);
      }
    }
  }

  /**
   * Abort all active uploads
   * @param {Array} uploads - Array of upload objects to abort
   */
  abortAllUploads(uploads) {
    if (uploads && uploads.length > 0) {
      uploads.forEach(uploadObj => {
        if (uploadObj.abort) {
          uploadObj.abort();
        }
      });
    }

    // Also abort any remaining active uploads
    this.activeUploads.forEach((upload, uploadId) => {
      this.abortUpload(uploadId);
    });
  }

  /**
   * Generate unique upload ID
   * @returns {string} Upload ID
   */
  generateUploadId() {
    return 'tus_upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Get download URL for uploaded file
   * @param {string} projectId - Project ID
   * @param {string} filename - File name
   * @returns {string} Download URL
   */
  getDownloadUrl(projectId, filename) {
    const baseUrl = this.endpoint.replace('/upload', '');
    const downloadUrl = `${baseUrl}/download/${projectId}/${filename}`;
    // Ensure download URL also uses HTTP protocol
    return this.enforceHttpProtocol(downloadUrl);
  }

  /**
   * Get list of projects (for debugging/management)
   * @returns {Promise<Array>} List of projects
   */
  async getProjects() {
    try {
      const url = this.enforceHttpProtocol(this.endpoint.replace('/upload', '/projects'));
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      return [];
    }
  }

  /**
   * Get files in a project (for debugging/management)
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} List of files
   */
  async getProjectFiles(projectId) {
    try {
      const url = this.enforceHttpProtocol(this.endpoint.replace('/upload', `/files/${projectId}`));
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch project files:', error);
      return [];
    }
  }

  /**
   * Get upload statistics
   * @returns {Object} Upload statistics
   */
  getUploadStats() {
    return {
      activeUploads: this.activeUploads.size,
      storedMetadata: this.uploadMetadata.size,
      endpoint: this.endpoint
    };
  }

  /**
   * Clean up expired URL storage entries
   */
  cleanupExpiredUrls() {
    try {
      const keys = Object.keys(localStorage);
      const tusUrlKeys = keys.filter(key => key.startsWith('tus_url_'));

      // Remove old entries (older than 24 hours)
      const dayAgo = Date.now() - (24 * 60 * 60 * 1000);

      tusUrlKeys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            // Simple cleanup - remove all old entries
            // In production, you might want more sophisticated cleanup
            localStorage.removeItem(key);
          }
        } catch (error) {
          console.warn('Failed to cleanup TUS URL:', error);
        }
      });

      console.log(`TUS: Cleaned up ${tusUrlKeys.length} stored URLs`);
    } catch (error) {
      console.warn('TUS: Failed to cleanup expired URLs:', error);
    }
  }

  /**
   * Test TUS server connection and configuration
   * @returns {Promise<Object>} Connection test result
   */
  async testConnection() {
    console.log('TUS: Testing server connection...');

    try {
      // Ensure we're testing the HTTP endpoint
      const testEndpoint = this.enforceHttpProtocol(this.endpoint);
      console.log('TUS: Testing endpoint:', testEndpoint);

      // Test if server responds to OPTIONS request (CORS preflight)
      const testResponse = await fetch(testEndpoint, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'upload-length, upload-metadata, tus-resumable'
        }
      });

      console.log('TUS: Server OPTIONS response status:', testResponse.status);
      console.log('TUS: Server response headers:', Object.fromEntries(testResponse.headers.entries()));

      // Check if TUS protocol is supported
      const tusVersion = testResponse.headers.get('tus-version');
      const tusResumable = testResponse.headers.get('tus-resumable');
      const tusExtensions = testResponse.headers.get('tus-extension');

      return {
        success: testResponse.ok,
        status: testResponse.status,
        tusVersion,
        tusResumable,
        tusExtensions,
        endpoint: testEndpoint,
        supportsPatch: testResponse.headers.get('access-control-allow-methods')?.includes('PATCH'),
        protocolCorrected: testEndpoint !== this.endpoint
      };
    } catch (error) {
      console.error('TUS: Server connection test failed:', error);
      return {
        success: false,
        error: error.message,
        endpoint: this.endpoint
      };
    }
  }
}

// Export singleton instance
export default new TusConstructionImageUploadService();
